/**
 * A tabela do SM: contorno, cabeçalho, células e a linha de vazio.
 *
 * Papel: reproduz `table.data` e o que vem com ela (l. 289–335, 407–409) —
 * `table-layout:fixed`, cabeçalho em mono 11 px caixa alta com borda de baixo de
 * 2 px, célula em Outfit 12 px 300 sem quebra e com reticências — e a linha de
 * "Nenhum registro encontrado." (l. 934), que também diz "Carregando…" e "Não
 * foi possível carregar.".
 *
 * Como usar: a largura de cada coluna é dada no `<th>` (`w-[13%]`), porque no
 * layout fixo é a primeira linha que decide; largura posta numa célula do corpo
 * não vale. O cabeçalho ordenável é o `CabecalhoOrdenavel` da base com
 * `className={cn(CLASSE_DO_CABECALHO_ORDENAVEL, 'w-[N%]')}`; o que não ordena é
 * `CelulaDeCabecalho`. A tabela não tem borda de cima: ela encosta na barra de
 * filtros sem filete entre as duas.
 *
 * O desenho no papel do Dashboard (l. 454–468) está aqui, sob a marca
 * `imprimindo-dashboard-de-manutencoes` que o `useImpressao` põe no `<body>`:
 * borda de cima, cabeçalho a 10 px com `8px 10px`, célula com `7px 10px`
 * alinhada ao meio em Outfit 400, e a linha que não se parte entre páginas. Sem
 * a marca, um Ctrl+P comum imprime a tabela como ela está na tela.
 */
import { type ComponentProps, type ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';
import { Botao } from './botao';

/** O cabeçalho, ordenável ou não. */
const CLASSE_DO_CABECALHO =
  'relative border-b-2 border-emb-borda bg-superficie-2 px-3 py-2.5 text-left font-mono text-[11px] font-medium tracking-[0.6px] text-texto-fraco uppercase print:[body[data-impressao=imprimindo-dashboard-de-manutencoes]_&]:px-2.5 print:[body[data-impressao=imprimindo-dashboard-de-manutencoes]_&]:py-2 print:[body[data-impressao=imprimindo-dashboard-de-manutencoes]_&]:text-[10px]';

/** O `className` do `CabecalhoOrdenavel` da base, com o hover de `th.sortable`. */
export const CLASSE_DO_CABECALHO_ORDENAVEL = `${CLASSE_DO_CABECALHO} hover:bg-emb-hover-bege`;

/** O que toda célula do corpo tem. */
const CLASSE_DA_CELULA =
  'font-outfit border-b border-emb-borda px-3 py-[9px] align-top text-[12px] font-light print:[body[data-impressao=imprimindo-dashboard-de-manutencoes]_&]:px-2.5 print:[body[data-impressao=imprimindo-dashboard-de-manutencoes]_&]:py-[7px] print:[body[data-impressao=imprimindo-dashboard-de-manutencoes]_&]:align-middle print:[body[data-impressao=imprimindo-dashboard-de-manutencoes]_&]:font-normal';

/** Os seis jeitos de célula da origem. */
export type TipoDeCelula = 'comum' | 'titulo' | 'fraco' | 'numero' | 'quebra' | 'acoes';

/** O que cada tipo acrescenta: quebra, cor, peso e alinhamento. */
const CLASSES_DO_TIPO: Readonly<Record<TipoDeCelula, string>> = {
  comum: 'overflow-hidden text-ellipsis whitespace-nowrap',
  titulo: 'overflow-hidden text-ellipsis whitespace-nowrap',
  fraco: 'overflow-hidden text-ellipsis whitespace-nowrap text-texto-fraco',
  numero: 'overflow-hidden font-bold text-ellipsis whitespace-nowrap',
  quebra: 'overflow-visible [text-overflow:clip] whitespace-normal [word-break:break-word]',
  acoes: 'overflow-visible text-center [text-overflow:clip] whitespace-nowrap',
};

/** O que a linha de vazio diz. */
export type SituacaoDaLinhaVazia = 'vazia' | 'carregando' | 'falhou';

/**
 * O contorno da tabela.
 *
 * @param children - O `<thead>` e o `<tbody>`.
 * @returns A tabela.
 */
export function Tabela({ children }: { children: ReactNode }) {
  return (
    <table className="w-full table-fixed border-collapse border border-t-0 border-emb-borda bg-superficie print:[body[data-impressao=imprimindo-dashboard-de-manutencoes]_&]:border-t">
      {children}
    </table>
  );
}

/**
 * Um cabeçalho que não ordena — o da coluna de ações.
 *
 * @param className - Classes de quem chama, como a largura da coluna.
 * @returns O `<th>`.
 */
export function CelulaDeCabecalho({ className, ...props }: ComponentProps<'th'>) {
  return <th scope="col" className={cn(CLASSE_DO_CABECALHO, className)} {...props} />;
}

/**
 * Uma célula do corpo.
 *
 * @param tipo - O jeito da célula; `comum` corta com reticências.
 * @param className - Classes de quem chama, aplicadas por último.
 * @returns O `<td>`.
 */
export function Celula({
  tipo = 'comum',
  className,
  ...props
}: ComponentProps<'td'> & { tipo?: TipoDeCelula }) {
  return <td className={cn(CLASSE_DA_CELULA, CLASSES_DO_TIPO[tipo], className)} {...props} />;
}

/**
 * Uma linha do corpo, branca, com o fundo cinza sob o ponteiro.
 *
 * @param children - As células.
 * @returns O `<tr>`.
 */
export function LinhaDaTabela({ children }: { children: ReactNode }) {
  return (
    <tr className="bg-superficie hover:bg-background print:[body[data-impressao=imprimindo-dashboard-de-manutencoes]_&]:break-inside-avoid print:[body[data-impressao=imprimindo-dashboard-de-manutencoes]_&]:bg-superficie">
      {children}
    </tr>
  );
}

/**
 * A linha única que ocupa a tabela quando não há linha para mostrar.
 *
 * O cabeçalho continua visível nos três casos, como na origem.
 *
 * @param colunas - Quantas colunas a tabela tem, para o `colSpan`.
 * @param situacao - `vazia` diz "Nenhum registro encontrado.", `carregando`
 *   diz "Carregando…" e `falhou` diz "Não foi possível carregar." com o botão.
 * @param onTentarDeNovo - Chamado pelo "Tentar de novo" da falha.
 * @returns O `<tr>`.
 */
export function LinhaVazia({
  colunas,
  situacao,
  onTentarDeNovo,
}: {
  colunas: number;
  situacao: SituacaoDaLinhaVazia;
  onTentarDeNovo: () => void;
}) {
  return (
    <tr className="bg-superficie">
      <td
        colSpan={colunas}
        className="border-b border-emb-borda p-6 text-center font-outfit text-[12px] font-light text-emb-vazio"
      >
        {situacao === 'vazia' && 'Nenhum registro encontrado.'}
        {situacao === 'carregando' && 'Carregando…'}
        {situacao === 'falhou' && (
          <>
            Não foi possível carregar.{' '}
            <Botao variante="fantasma" pequeno onClick={onTentarDeNovo}>
              Tentar de novo
            </Botao>
          </>
        )}
      </td>
    </tr>
  );
}
